'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import storage from 'local-storage-fallback';
import { AUTH, CLIENT_HEADER } from 'src/constants';
import { useAppDispatch } from 'src/redux/hooks';
import { useAuth } from 'src/context/auth';
import {
  territoryApi,
  exportsApi,
  strategyApi,
  integrationsApi,
  notificationsApi,
} from 'src/api/redux';
import { territoryChatApi } from 'src/api/redux/territoryOptimizationChat';
import { playsApi } from 'src/api/redux/strategy/playsApi';
import { showWebSocketToast } from 'src/components/WebSockets';
import { TERRITORY_OPTIMIZATION_ACCESS } from 'src/components/PermissionGuard/checks';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { setApiError } from 'src/redux/slices/apiErrorSlice';
import { parseRetryAfterMs } from 'src/api/retryAfter';
import { addBreadcrumb } from 'src/observability';

// --- Types ---

interface WebSocketNotification {
  id: number;
  category: string;
  severity: string;
  title: string;
  body: string;
  source_content_type: string | null;
  source_object_id: number | null;
  read_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

interface WebSocketEnvelope {
  type: string;
  data: WebSocketNotification;
}

// --- Backoff helpers ---

const BACKOFF_BASE_MS = 1000;
const BACKOFF_MAX_MS = 30000;

// Minimum time (ms) the connection must have been down before we invalidate
// all caches on reconnect. Brief disconnects (network blips, WS restarts)
// are unlikely to have missed meaningful events and don't warrant nuking
// every active query cache.
export const RECONNECT_INVALIDATION_THRESHOLD_MS = 5000;

export function getBackoffDelay(attempt: number): number {
  const exponential = Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_MAX_MS);
  const jitter = exponential * (0.5 + Math.random() * 0.5);
  return jitter;
}

// --- URL helper ---

/** Derive the WebSocket base URL from `NEXT_PUBLIC_API_URL`.
 *  e.g. `https://api.medscout.io/api/` → `wss://api.medscout.io/ws/` */
export function deriveWsUrl(): string {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? '';
  return apiUrl
    .replace(/^https?/, (proto) => (proto === 'https' ? 'wss' : 'ws'))
    .replace(/\/api\/?$/, '/ws/');
}

// --- Token helper ---

export function getAccessToken(): string | null {
  try {
    const raw = storage.getItem(AUTH.ACCESS_TOKEN);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return storage.getItem(AUTH.ACCESS_TOKEN);
  }
}

// --- Rate limit error ---

export class RateLimitError extends Error {
  readonly status = 429;
  readonly retryAfterMs: number | null;

  constructor(retryAfterMs: number | null) {
    super('Ticket fetch failed: 429');
    this.retryAfterMs = retryAfterMs;
  }
}

// --- Ticket helper ---

export async function fetchTicket(): Promise<string> {
  const token = getAccessToken();
  if (!token) throw new Error('No access token');

  const baseUrl = (process.env.NEXT_PUBLIC_API_URL ?? '').replace(/\/$/, '');
  const response = await fetch(`${baseUrl}/v1/events/auth/ticket/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      // Ticket fetch reaches medscout-api directly (MKT-455 / TG-2).
      [CLIENT_HEADER.NAME]: CLIENT_HEADER.WEB,
    },
  });

  if (response.status === 429) {
    const retryAfterMs = parseRetryAfterMs(
      response.headers.get('Retry-After')
    );
    throw new RateLimitError(retryAfterMs);
  }

  if (!response.ok) throw new Error(`Ticket fetch failed: ${response.status}`);

  const data = await response.json();
  return data.ticket;
}

// --- Category dispatch map ---

type InvalidationDispatcher = (
  dispatch: ReturnType<typeof useAppDispatch>
) => void;

export const CATEGORY_INVALIDATIONS: Record<string, InvalidationDispatcher> = {
  territory_optimization: (dispatch) => {
    dispatch(territoryApi.util.invalidateTags(['Territory']));
    dispatch(territoryChatApi.util.invalidateTags(['Thread']));
  },
  export: (dispatch) => {
    dispatch(exportsApi.util.invalidateTags(['Exports']));
  },
  strategy: (dispatch) => {
    dispatch(strategyApi.util.invalidateTags(['Strategy', 'STRATEGY_METRICS']));
    dispatch(playsApi.util.invalidateTags(['PLAYS', 'STRATEGY_METRICS']));
  },
  crm_sync: (dispatch) => {
    // Not CRM_OPERATION_FAILURES: the bulk sync path passes record_failures=False,
    // so a completed sync never writes one.
    dispatch(integrationsApi.util.invalidateTags(['CRM_SYNC_STATUS']));
  },
  encounters: () => {
    // Encounters uses React Context, no RTK Query tags to invalidate.
  },
};

export function invalidateAllTags(
  dispatch: ReturnType<typeof useAppDispatch>
): void {
  Object.values(CATEGORY_INVALIDATIONS).forEach((fn) => fn(dispatch));
}

// --- Availability ---

/**
 * Whether WebSocket notifications are available to the current user.
 *
 * `enableWebsockets` is the rollout switch for the notification system as a
 * whole. Territory Optimization jobs run long and announce completion over this
 * same socket, so the RBAC code is a second, independent way in: holding it
 * turns notifications on **even where the flag is still off** (MKT-545). The
 * connection, the toast, the bell, and the Notifications page all turn on
 * together, carrying every category rather than just `territory_optimization` —
 * so a permission holder at a company the rollout has not reached also starts
 * receiving export, strategy, CRM-sync and encounters notifications.
 *
 * Every `enableWebsockets` gate in the app goes through this hook, so the five
 * surfaces can't drift into a half-wired state (a bell with no socket behind it,
 * or a socket with nowhere to surface what it receives).
 */
export const useWebSocketNotificationsEnabled = (): boolean => {
  const { enableWebsockets } = useFlags();
  const { hasPermission } = useAuthorization();

  return !!enableWebsockets || hasPermission(TERRITORY_OPTIMIZATION_ACCESS);
};

// --- Hook ---

const useWebSocket = (): void => {
  const dispatch = useAppDispatch();
  const notificationsEnabled = useWebSocketNotificationsEnabled();
  const { user } = useAuth();
  const userId = user?.id;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const attemptRef = useRef(0);
  const hasConnectedRef = useRef(false);
  // Bumped on every effect run; used to invalidate any in-flight connect()
  // whose `await fetchTicket()` started under a stale mount.
  const epochRef = useRef(0);
  // Timestamp when the connection was lost — used to decide whether a
  // reconnect should invalidate caches (only if down long enough to have
  // plausibly missed events).
  const disconnectedAtRef = useRef<number | null>(null);

  const connect = useCallback(async () => {
    const myEpoch = epochRef.current;

    if (!mountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

    let ticket: string;
    try {
      ticket = await fetchTicket();
    } catch (err) {
      if (myEpoch !== epochRef.current || !mountedRef.current) return;

      // Stop reconnecting on 429 — retrying would worsen the rate limit
      if (err instanceof RateLimitError) {
        addBreadcrumb({
          category: 'websocket',
          message: 'Rate limited (429) on ticket fetch, not reconnecting',
          level: 'warning',
          data: { retryAfterMs: err.retryAfterMs },
        });
        dispatch(
          setApiError({
            reason: 'rate_limited',
            ...(err.retryAfterMs != null && {
              retryAfterMs: err.retryAfterMs,
            }),
          })
        );
        return;
      }

      addBreadcrumb({
        category: 'websocket',
        message: 'Ticket fetch failed',
        level: 'warning',
        data: { error: err instanceof Error ? err.message : String(err) },
      });

      const delay = getBackoffDelay(attemptRef.current);
      attemptRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(connect, delay);
      return;
    }

    if (myEpoch !== epochRef.current || !mountedRef.current) return;

    const wsUrl = `${process.env.NEXT_PUBLIC_WS_URL}?ticket=${ticket}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      const isReconnect = hasConnectedRef.current;
      hasConnectedRef.current = true;
      attemptRef.current = 0;

      if (isReconnect) {
        const downtime = disconnectedAtRef.current
          ? Date.now() - disconnectedAtRef.current
          : 0;

        if (downtime >= RECONNECT_INVALIDATION_THRESHOLD_MS) {
          invalidateAllTags(dispatch);
        }
      }
      disconnectedAtRef.current = null;
    };

    ws.onmessage = (event) => {
      let envelope: WebSocketEnvelope;
      try {
        envelope = JSON.parse(event.data);
      } catch {
        addBreadcrumb({
          category: 'websocket',
          message: 'Failed to parse WebSocket message',
          level: 'warning',
        });
        return;
      }

      const data = envelope.data;

      // Invalidate notifications list and unread count for every incoming message
      dispatch(
        notificationsApi.util.invalidateTags([
          { type: 'Notifications', id: 'LIST' },
          { type: 'Notifications', id: 'UNREAD_COUNT' },
        ])
      );

      const handler = CATEGORY_INVALIDATIONS[data.category];
      if (handler) {
        handler(dispatch);
        showWebSocketToast(data);
      } else {
        addBreadcrumb({
          category: 'websocket',
          message: `Unknown notification category: ${data.category}`,
          level: 'warning',
        });
      }
    };

    ws.onclose = (event) => {
      wsRef.current = null;
      disconnectedAtRef.current ??= Date.now();

      if (event.code === 4001) {
        addBreadcrumb({
          category: 'websocket',
          message: 'Unauthorized (4001), not reconnecting',
          level: 'warning',
        });
        return;
      }

      if (event.code !== 1000) {
        addBreadcrumb({
          category: 'websocket',
          message: `Connection closed: code=${event.code} reason="${event.reason || 'none'}"`,
          level: 'warning',
        });
      }

      if (mountedRef.current && getAccessToken()) {
        const delay = getBackoffDelay(attemptRef.current);
        attemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [dispatch]);

  useEffect(() => {
    mountedRef.current = true;
    epochRef.current += 1;

    if (notificationsEnabled && userId) {
      connect();
    }

    return () => {
      mountedRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [notificationsEnabled, userId, connect]);
};

export default useWebSocket;
